import { useEffect, useRef, useCallback } from 'react';

import { TokenManager, authApi } from '@tankef/api-client';

// Renovar 60 segundos antes de expirar
const REFRESH_MARGIN_MS = 60 * 1000;

/**
 * Hook que maneja la renovación proactiva de tokens.
 *
 * Programa un timer para renovar el access token antes de que expire,
 * evitando que el usuario sea deslogueado inesperadamente.
 *
 * Debe usarse en el componente raíz de la aplicación (ProtectedRoute).
 */
export function useTokenRefresh() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef(false);
  const scheduleRefreshRef = useRef<(() => void) | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const performRefresh = useCallback(async (): Promise<boolean> => {
    // Evitar múltiples refreshes simultáneos
    if (isRefreshingRef.current) return false;

    isRefreshingRef.current = true;
    try {
      await authApi.refreshToken();
      isRefreshingRef.current = false;
      return true;
    } catch {
      isRefreshingRef.current = false;
      // Refresh falló - el interceptor de 401 manejará el logout
      return false;
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    clearTimer();

    // Si no hay sesión válida o no hay refresh token, no programar
    const accessToken = TokenManager.getAccessToken();
    const hasRefreshToken = TokenManager.hasRefreshToken();

    if (!accessToken || !hasRefreshToken) {
      return;
    }

    // Calcular tiempo hasta refresh (tiempo restante - margen)
    const timeRemainingMs = TokenManager.getTokenTimeRemaining() * 1000;
    const refreshIn = timeRemainingMs - REFRESH_MARGIN_MS;

    // Si ya expiró, no hacer nada - el interceptor de 401 lo manejará
    if (refreshIn <= 0) {
      return;
    }

    // Programar refresh
    timerRef.current = setTimeout(async () => {
      const success = await performRefresh();
      if (success) {
        // Reprogramar para el próximo ciclo
        scheduleRefreshRef.current?.();
      }
    }, refreshIn);
  }, [clearTimer, performRefresh]);

  // Mantener la función actualizada en el ref
  useEffect(() => {
    scheduleRefreshRef.current = scheduleRefresh;
  }, [scheduleRefresh]);

  useEffect(() => {
    // Programar refresh inicial
    scheduleRefresh();

    // Escuchar cambios en storage (login/logout en otra pestaña)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'tankef_auth_token' || e.key === 'tankef_expires_at') {
        // Pequeño delay para evitar race conditions
        setTimeout(() => scheduleRefresh(), 100);
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      clearTimer();
      window.removeEventListener('storage', handleStorage);
    };
  }, [scheduleRefresh, clearTimer]);

  return { scheduleRefresh, clearTimer };
}
